import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Flex, Typo, theme } from '../../../../ui';
import Icon from '../../../../ui/Icon';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, toRgba } from '../../../../ui/theme/colors';
import CoursesListCarousel from '../CoursesListCarousel';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${(props) => props.$brandColor};
  border-radius: 8px;
  color: ${(props) => props.$brandColor};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    background: ${(props) => toRgba(props.$brandColor, 0.1)};
  }
`;

const EmptyState = styled(Flex)`
  padding: 40px 20px;
  text-align: center;
`;

const EditableCoursesWidget = ({ courses = [] }) => {
  const navigate = useNavigate();
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;

  const editableCourses = courses.filter((course) => course.canEdit);

  const handleViewAll = useCallback(() => {
    navigate('/courses');
  }, [navigate]);

  if (!editableCourses.length) {
    return (
      <WidgetContainer direction='column' align='flex-start'>
        <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Your Courses
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>Courses you can edit and manage</Typo.Info>
          </Flex>
          <Icon.Course size={'medium'} color={brandColor} />
        </Flex>
        <EmptyState direction='column' align='center' justify='center' width='100%'>
          <Icon.Course size={'large'} color={theme.colors.NEU_300} />
          <Typo.MediumText color={theme.colors.NEU_600} style={{ marginTop: '16px' }}>
            No courses to display
          </Typo.MediumText>
          <Typo.Info color={theme.colors.NEU_300} style={{ marginTop: '8px' }}>
            Create a new course to get started
          </Typo.Info>
        </EmptyState>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <CoursesListCarousel
        courses={editableCourses}
        header={
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Your Courses
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>
              {editableCourses.length} course{editableCourses.length !== 1 ? 's' : ''} you can edit
            </Typo.Info>
          </Flex>
        }
        headerRight={
          <ViewAllButton onClick={handleViewAll} $brandColor={brandColor}>
            View All
            <Icon.ArrowRight size={'x-small'} />
          </ViewAllButton>
        }
      />
    </WidgetContainer>
  );
};

export default EditableCoursesWidget;
